import Sortable from "sortablejs";
import { Toast } from "../../utils/Toast";
import { Modal } from "../../utils/Modal";

/**
 * CMS 콘텐츠 탭 정렬 — [data-cms-sortable] 목록(섹션/요소) 각각을 독립 SortableJS 로.
 * 직계 자식([data-id])을 순서대로 {id, sort_order} 직렬화 → data-reorder-url 에 PATCH(override).
 * 섹션과 요소는 그룹을 공유하지 않으므로 서로 섞이지 않는다.
 */
class CmsSortableList {
    readonly #el: HTMLElement;
    readonly #endpoint: string;
    readonly #csrf: string;

    constructor(el: HTMLElement) {
        this.#el = el;
        this.#endpoint = el.dataset.reorderUrl ?? "";
        this.#csrf =
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ??
            document.querySelector<HTMLInputElement>('input[name="_token"]')?.value ?? "";
    }

    init(): void {
        if (!this.#endpoint) return;
        Sortable.create(this.#el, {
            animation: 150,
            handle: "[data-cms-drag-handle]",
            draggable: "[data-id]",
            ghostClass: "admin-page-tree__ghost",
            dragClass: "admin-page-tree__drag",
            onEnd: () => void this.#save(),
        });
    }

    #serialize(): Array<{ id: number; sort_order: number }> {
        const out: Array<{ id: number; sort_order: number }> = [];
        Array.from(this.#el.children).forEach((child, i) => {
            const id = parseInt((child as HTMLElement).dataset.id ?? "0", 10);
            if (id) out.push({ id, sort_order: (i + 1) * 10 });
        });
        return out;
    }

    async #save(): Promise<void> {
        const items = this.#serialize();
        if (items.length === 0) return;
        document.documentElement.classList.add("admin--loading");
        try {
            const res = await fetch(this.#endpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                    "X-HTTP-Method-Override": "PATCH",
                    "X-CSRF-TOKEN": this.#csrf,
                },
                body: JSON.stringify({ items, _token: this.#csrf }),
            });
            const json = (await res.json().catch(() => ({}))) as { result?: string; msg?: string };
            if (res.ok && json.result === "success") {
                Toast.success("순서가 저장되었습니다.");
            } else {
                await Modal.alert(json.msg ?? "순서 저장 실패. 새로고침 후 다시 시도해주세요.");
                window.location.reload();
            }
        } catch {
            await Modal.alert("순서 저장 중 오류가 발생했습니다.");
            window.location.reload();
        } finally {
            document.documentElement.classList.remove("admin--loading");
        }
    }
}

interface SortRoot extends HTMLElement { _cmsSortInited?: boolean; }

export function initCmsContentSort(): void {
    document.querySelectorAll<SortRoot>("[data-cms-sortable]").forEach((el) => {
        if (el._cmsSortInited) return;
        el._cmsSortInited = true;
        new CmsSortableList(el).init();
    });
}
